import React, { useEffect, useState } from "react"
import { get } from "helpers/api_helper"
import { familyName, supplierNames } from "./components/Tabs/Logistics/labels"
import { officialBallChosen } from "./eventCreationForm"
import "./components/Tabs/Logistics/logistics.scss"

// The product a card line offers under this id, if any.
const optionOf = (line, id) => line.options.find(option => option.id === id)
// A Tenis10 card's rows in the sheet's order (board F-A): the lines the API resolved, and the balls and the lanyards that come later.
const tenis10Rows = lines => TENIS10_ORDER.map(family => TENIS10_COLOUR_ROWS[family] ? { family, colour: TENIS10_COLOUR_ROWS[family] }
  : lines.find(line => line.family === family)).filter(Boolean)

// The Logistics card of the event creation (ticket 15, Q6, Q7 and Q15; PRD US 2): each generic family
// with its default product for the chosen sport, category and official ball (the oldest active one that
// fits and still has stock available, otherwise the oldest), and its supplier. When several products fit, such as two batches, the card
// offers them; a product or a supplier changed here is chosen explicitly. Balls no catalogue product
// covers stay with the club, so a choice made for them goes when the ball changes, as does a product
// the new choices no longer offer. `onStatus` reports whether the card has loaded.
// A Tenis10 event (`programType`, PRD 16 LR15, the canvas verdict E-A, round 7 „A · insigna MPG”) reads the Tenis10 products of the
// catalogue, whatever its category and with no official ball: the medals, the diplomas and the water; its balls (the club's) and its
// lanyards (MPG's) follow the colours of the competitions, added after creation, so the card names them without a choice.
const TENIS10_COLOUR_ROWS = {
  balls: { product: "Tenis10 balls of each competition’s colour", rule: "4 per group, one group per court · a recommendation", supplier: "club", locked: true },
  lanyards: { product: "Tenis10 lanyards of each competition’s colour", rule: "4 per competition", supplier: "mpg" },
}
const TENIS10_RULES = { medals: "Places 1–4 of each competition", diplomas: "1 per child of the Main List" }
const TENIS10_ORDER = ["balls", "medals", "lanyards", "diplomas", "water"]

export default function CreateEventLogistics({ sportType, programType, category, officialBall, suppliers, onSuppliers, products, onProducts, onStatus }) {
  // The products resolve once the sport, the category and the official ball are all decided; a Tenis10 event's need no ball.
  const tenis10 = Boolean(programType)
  const decided = Boolean(sportType && category && (tenis10 || officialBallChosen(officialBall)))
  const ball = tenis10 ? "" : officialBall?.name || ""
  const [state, setState] = useState({ status: "idle" })
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    if (!decided) { setState({ status: "idle" }); return undefined }
    let active = true
    setState({ status: "loading" })
    get("/admin/logistics/generic-products", { params: tenis10 ? { sportType, programType, category } : { sportType, category, officialBall: ball } }).then(response => {
      if (!Array.isArray(response?.data?.lines)) throw new Error("Invalid Logistics card")
      if (active) setState({ status: "ready", lines: response.data.lines.map(line => ({ ...line, options: line.options || [] })) })
    }).catch(() => { if (active) setState({ status: "error" }) })
    return () => { active = false }
  }, [decided, sportType, programType, tenis10, category, ball, attempt])
  useEffect(() => { onStatus(state.status) }, [state.status, onStatus])
  useEffect(() => {
    const lines = state.lines || []
    // A supplier goes with a line without a product, and with a family the card no longer has (the balls of an event that became Tenis10).
    const withoutProduct = Object.keys(suppliers).filter(family => state.lines && !lines.some(line => line.family === family && line.product))
    if (withoutProduct.length) onSuppliers(Object.fromEntries(Object.entries(suppliers).filter(([family]) => !withoutProduct.includes(family))))
  }, [state.lines, suppliers, onSuppliers])
  useEffect(() => {
    if (!state.lines) return
    const kept = Object.entries(products).filter(([family, id]) => state.lines.some(line => line.family === family && optionOf(line, id)))
    if (kept.length !== Object.keys(products).length) onProducts(Object.fromEntries(kept))
  }, [state.lines, products, onProducts])

  const productLabel = line => line.product?.name || (line.family !== "balls" ? "No catalogue product" : ball ? `${ball} — no catalogue product` : "Balls TBD")
  // The product a line has now: the one chosen here, otherwise its default.
  const productOf = line => optionOf(line, products[line.family]) || line.product && { ...line.product, supplier: line.supplier }
  // A product set back to the default follows the catalogue again, and so does a supplier set back to its product's.
  const chooseProduct = (line, id) => {
    const { [line.family]: previous, ...others } = products
    onProducts(id === line.product.id ? others : { ...others, [line.family]: id })
    const supplier = optionOf(line, id)?.supplier
    if (suppliers[line.family] === supplier) chooseSupplier(line.family, supplier, supplier)
  }
  const chooseSupplier = (family, supplier, catalogue) => {
    const { [family]: previous, ...others } = suppliers
    onSuppliers(supplier === catalogue ? others : { ...others, [family]: supplier })
  }
  const productCell = line => {
    if (!line.product || line.options.length < 2) return productLabel(line)
    const name = familyName(line.family)
    return <>
      <select className="form-control form-control-sm" aria-label={`Product of ${name}`} value={productOf(line).id}
        onChange={event => chooseProduct(line, event.target.value)}>
        {line.options.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
      </select>
      {products[line.family] && <small className="text-muted d-block">Product chosen explicitly</small>}
    </>
  }
  const supplierCell = line => {
    if (!line.product) return <>
      <span className="badge badge-soft-secondary p-2">{supplierNames[line.supplier] || line.supplier}<i className="bx bxs-lock-alt ml-1" aria-hidden="true" /></span>
      <small className="text-muted d-block">{ball ? "MPG needs a catalogue product for this ball" : "MPG needs a decided official ball"}</small>
    </>
    const catalogue = productOf(line).supplier
    return <>
      <select className="form-control form-control-sm" aria-label={`Supplier of ${familyName(line.family)}`} value={suppliers[line.family] || catalogue}
        onChange={event => chooseSupplier(line.family, event.target.value, catalogue)}>
        {Object.entries(supplierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      {suppliers[line.family] && suppliers[line.family] !== catalogue && <small className="text-muted d-block">Chosen explicitly</small>}
    </>
  }
  return <section className="creation-section" aria-label="Logistics" id="create-logistics" tabIndex={-1}>
    <h5><i className="bx bx-package mr-1" aria-hidden="true" />Logistics</h5>
    <p className="text-muted">SuperAdmin only · creates the event's logistics sheet.</p>
    {state.status === "idle" && <p className="creation-note">{tenis10 ? "Choose the category to see the products." : "Choose the category and the official ball to see the products."}</p>}
    {state.status === "loading" && <p role="status">Loading the Logistics products…</p>}
    {state.status === "error" && <div className="alert alert-danger" role="alert">
      The Logistics products could not be loaded.{" "}
      <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setAttempt(value => value + 1)}>Retry</button>
    </div>}
    {state.status === "ready" && <table className="table table-sm logistics-stack mb-2">
      <thead><tr><th>Standard product</th><th>Resolved product</th><th>Supplier</th></tr></thead>
      <tbody>{(tenis10 ? tenis10Rows(state.lines) : state.lines).map(line => line.colour ? <tr key={line.family}>
        <td><strong>{familyName(line.family)}</strong></td>
        <td data-label="Resolved product">{line.colour.product}<small className="text-muted d-block">{line.colour.rule}</small></td>
        <td data-label="Supplier"><span className="badge badge-soft-secondary p-2">{supplierNames[line.colour.supplier]}
          {line.colour.locked && <i className="bx bxs-lock-alt ml-1" aria-hidden="true" />}</span></td>
      </tr> : <tr key={line.family}>
        <td><strong>{familyName(line.family)}</strong></td>
        <td data-label="Resolved product">{productCell(line)}{tenis10 && TENIS10_RULES[line.family] && <small className="text-muted d-block">{TENIS10_RULES[line.family]}</small>}</td>
        <td data-label="Supplier">{supplierCell(line)}</td>
      </tr>)}</tbody>
    </table>}
    <p className="creation-note mb-0">{tenis10 ? "Tenis10: the Tenis10 products of the catalogue, whatever the category. The balls and the lanyards follow the "
      + "colours of the competitions, added after creation." : "Defaults come from the catalogue and the stock: the oldest active product that fits and "
      + "still has stock available, otherwise the oldest, with its supplier. A product or a supplier changed here is chosen explicitly, so later "
      + "catalogue changes do not overwrite it. After creation, both change only in the event's Logistics tab."}</p>
  </section>
}
